import type { CasingIntervalGeometry } from "@/libs/calculations/casingFrictionLoss";
import {
  computeCasingPressurePropertiesBetweenPerfsAndDatum,
  computeCasingPressurePropertiesBetweenPerfsAndBottomOfEquipmentUpward,
  computeCasingPressurePropertiesBetweenPerfsAndBottomOfEquipmentDownward,
  computeCasingPressurePropertiesBetweenSensorAndMotor,
  computeCasingPressurePropertiesBetweenMotorAndSeals,
  computeCasingPressurePropertiesBetweenSealsAndIntakes,
  resolveCasingInteriorDiameterAtDepth
} from "@/libs/calculations/casingFrictionLoss";
import type { Correlations } from "../forms/formTypes";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";
import type { DeviationSurveyEntryRows } from "./calculationTypes";

export const buildIntervalCasingGeometry = (
  startMDFt: number,
  endMDFt: number,
  topCasingInteriorDiameterIn: number,
  topCasingLengthFt: number,
  bottomCasingInteriorDiameterIn: number,
  bottomCasingLengthFt: number,
  effectiveCasingDiameterIn?: number,
  annulusDiameterIn?: number
): CasingIntervalGeometry => {
  // Get casing ID at start/end depths regardless of shroud
  const rawCasingInteriorDiameterAtStart = resolveCasingInteriorDiameterAtDepth(
    startMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  );
  const rawCasingInteriorDiameterAtEnd = resolveCasingInteriorDiameterAtDepth(
    endMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  );

  // Assume start and end have the same effective casing diameter since
  // shroud is the only thing to supplant the casing ID
  const effectiveCasingDiameterAtStart =
    effectiveCasingDiameterIn || rawCasingInteriorDiameterAtStart;
  const effectiveCasingDiameterAtEnd =
    effectiveCasingDiameterIn || rawCasingInteriorDiameterAtEnd;

  // We will assume the start and end passed in will always have the same annulus
  const annulusDiameterAtStart = annulusDiameterIn;
  const annulusDiameterAtEnd = annulusDiameterIn;

  return {
    rawCasingInteriorDiameterAtStart,
    rawCasingInteriorDiameterAtEnd,
    effectiveCasingDiameterAtStart,
    effectiveCasingDiameterAtEnd,
    annulusDiameterAtStart,
    annulusDiameterAtEnd,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  };
};

export type PerfsAndDatumCasingParams = {
  wellFlowingPressureAtDatumPSIA: number;
  correlations: Correlations;
  datumPointFt: number;
  datumPointTVD: number;
  topOfPerforationsMDFt: number;
  topOfPerforationsTVDFt: number;
  entryRows: DeviationSurveyEntryRows;
  fluidPropertiesAtDatumContext: FluidPropertiesContext;
  surfaceTemperatureF: number;
  bottomholeTemperatureF: number;
  casingGeometry: CasingIntervalGeometry;
  bottomCasingPressurePSIA: number;
  producingBottomholePressurePSIA: number;
};

export const calculatePerfsAndDatumPressure = (
  params: PerfsAndDatumCasingParams
) => {
  return computeCasingPressurePropertiesBetweenPerfsAndDatum(
    params.wellFlowingPressureAtDatumPSIA,
    params.correlations,
    params.datumPointFt,
    params.datumPointTVD,
    params.topOfPerforationsMDFt,
    params.topOfPerforationsTVDFt,
    params.entryRows,
    params.fluidPropertiesAtDatumContext,
    params.surfaceTemperatureF,
    params.bottomholeTemperatureF,
    params.casingGeometry,
    params.bottomCasingPressurePSIA,
    params.producingBottomholePressurePSIA,
    true
  );
};

export type BottomOfEquipmentCasingParams = {
  correlations: Correlations;
  topOfPerforationsMDFt: number;
  topOfPerforationsTVDFt: number;
  bottomOfEquipmentMDFt: number;
  bottomOfEquipmentTVDFt: number;
  entryRows: DeviationSurveyEntryRows;
  fluidPropertiesAtWellPerfsContext: FluidPropertiesContext;
  surfaceTemperatureF: number;
  bottomholeTemperatureF: number;
  casingGeometry: CasingIntervalGeometry;
  bottomCasingPressurePSIA: number;
  producingBottomholePressurePSIA: number;
  wellFlowingPressureAtPerfsPSIA: number;
  useShroudCirculationPath: boolean;
  shroudOuterDiameterIn?: number;
  bottomOfTubingMDFt: number;
  bottomOfTubingTVDFt: number;
  boltOnDischargeOuterDiameterInches: number;
  topOfPumpsMDFt: number;
  topOfPumpsTVDFt: number;
  bottomOfPumpsMDFt: number;
  bottomOfPumpsTVDFt: number;
  topTubingOuterDiameterInches: number;
  bottomTubingOuterDiameterInches: number;
  topTubingLengthFt: number;
  primaryPumpOuterDiameterInches: number;
  includeCasingFrictionLoss: boolean;
  getPressureAtReferenceTVDPSIA: (referenceTVDFt: number) => number;
};

export const calculateBottomOfEquipmentCasingFriction = (
  params: BottomOfEquipmentCasingParams
) => {
  // Changes on if perfs are above or below the bottom of equipment
  return params.useShroudCirculationPath
    ? computeCasingPressurePropertiesBetweenPerfsAndBottomOfEquipmentDownward(
        params.wellFlowingPressureAtPerfsPSIA,
        params.correlations,
        params.topOfPerforationsMDFt,
        params.topOfPerforationsTVDFt,
        params.bottomOfTubingMDFt,
        params.bottomOfTubingTVDFt,
        params.boltOnDischargeOuterDiameterInches,
        params.topOfPumpsMDFt,
        params.topOfPumpsTVDFt,
        params.bottomOfPumpsMDFt,
        params.bottomOfPumpsTVDFt,
        params.bottomOfEquipmentMDFt,
        params.bottomOfEquipmentTVDFt,
        params.topTubingOuterDiameterInches,
        params.bottomTubingOuterDiameterInches,
        params.topTubingLengthFt,
        params.primaryPumpOuterDiameterInches,
        params.shroudOuterDiameterIn!,
        params.entryRows,
        params.fluidPropertiesAtWellPerfsContext,
        params.surfaceTemperatureF,
        params.bottomholeTemperatureF,
        params.casingGeometry,
        params.bottomCasingPressurePSIA,
        params.producingBottomholePressurePSIA,
        params.includeCasingFrictionLoss,
        params.getPressureAtReferenceTVDPSIA
      ).casingFrictionProperties
    : computeCasingPressurePropertiesBetweenPerfsAndBottomOfEquipmentUpward(
        params.correlations,
        params.topOfPerforationsMDFt,
        params.topOfPerforationsTVDFt,
        params.bottomOfEquipmentMDFt,
        params.bottomOfEquipmentTVDFt,
        params.entryRows,
        params.fluidPropertiesAtWellPerfsContext,
        params.surfaceTemperatureF,
        params.bottomholeTemperatureF,
        params.casingGeometry,
        params.bottomCasingPressurePSIA,
        params.producingBottomholePressurePSIA
      );
};

export type SensorAndMotorCasingParams = {
  correlations: Correlations;
  bottomOfSensorMDFt: number;
  bottomOfSensorTVDFt: number;
  bottomOfMotorMDFt: number;
  bottomOfMotorTVDFt: number;
  entryRows: DeviationSurveyEntryRows;
  fluidPropertiesAtSensorContext: FluidPropertiesContext;
  surfaceTemperatureF: number;
  bottomholeTemperatureF: number;
  casingGeometry: CasingIntervalGeometry;
  bottomCasingPressurePSIA: number;
  producingBottomholePressurePSIA: number;
};

export const calculateSensorAndMotorCasingFriction = (
  params: SensorAndMotorCasingParams
) => {
  return computeCasingPressurePropertiesBetweenSensorAndMotor(
    params.correlations,
    params.bottomOfSensorMDFt,
    params.bottomOfSensorTVDFt,
    params.bottomOfMotorMDFt,
    params.bottomOfMotorTVDFt,
    params.entryRows,
    params.fluidPropertiesAtSensorContext,
    params.surfaceTemperatureF,
    params.bottomholeTemperatureF,
    params.casingGeometry,
    params.bottomCasingPressurePSIA,
    params.producingBottomholePressurePSIA
  );
};

export type MotorAndSealsCasingParams = {
  correlations: Correlations;
  bottomOfMotorMDFt: number;
  bottomOfMotorTVDFt: number;
  bottomOfSealsMDFt: number;
  bottomOfSealsTVDFt: number;
  entryRows: DeviationSurveyEntryRows;
  fluidPropertiesAtMotorContext: FluidPropertiesContext;
  surfaceTemperatureF: number;
  bottomholeTemperatureF: number;
  casingGeometry: CasingIntervalGeometry;
  bottomCasingPressurePSIA: number;
  producingBottomholePressurePSIA: number;
};

export const calculateMotorAndSealsCasingFriction = (
  params: MotorAndSealsCasingParams
) => {
  return computeCasingPressurePropertiesBetweenMotorAndSeals(
    params.correlations,
    params.bottomOfMotorMDFt,
    params.bottomOfMotorTVDFt,
    params.bottomOfSealsMDFt,
    params.bottomOfSealsTVDFt,
    params.entryRows,
    params.fluidPropertiesAtMotorContext,
    params.surfaceTemperatureF,
    params.bottomholeTemperatureF,
    params.casingGeometry,
    params.bottomCasingPressurePSIA,
    params.producingBottomholePressurePSIA
  );
};

export type SealsAndIntakesCasingParams = {
  correlations: Correlations;
  bottomOfSealsMDFt: number;
  bottomOfSealsTVDFt: number;
  bottomOfIntakesMDFt: number;
  bottomOfIntakesTVDFt: number;
  entryRows: DeviationSurveyEntryRows;
  fluidPropertiesAtSealsContext: FluidPropertiesContext;
  surfaceTemperatureF: number;
  bottomholeTemperatureF: number;
  casingGeometry: CasingIntervalGeometry;
  bottomCasingPressurePSIA: number;
  producingBottomholePressurePSIA: number;
};

export const calculateSealsAndIntakesCasingFriction = (
  params: SealsAndIntakesCasingParams
) => {
  return computeCasingPressurePropertiesBetweenSealsAndIntakes(
    params.correlations,
    params.bottomOfSealsMDFt,
    params.bottomOfSealsTVDFt,
    params.bottomOfIntakesMDFt,
    params.bottomOfIntakesTVDFt,
    params.entryRows,
    params.fluidPropertiesAtSealsContext,
    params.surfaceTemperatureF,
    params.bottomholeTemperatureF,
    params.casingGeometry,
    params.bottomCasingPressurePSIA,
    params.producingBottomholePressurePSIA
  );
};
